import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { ProductSearchGateway } from '../gateway/product-search.gateway';
import {
  GET_PRODUCT_SEARCH_PAGE,
  LOAD_PRODUCT_SEARCH_PAGE,
  loadProductSearchPage,
  loadProductSearchPageFail,
  loadProductSearchPageSuccess,
} from '../actions/product-search.action';
import {
  catchError,
  exhaustMap,
  first,
  map,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { BoelsState } from '../../reducers.index';
import { CultureFacade } from '../../cultures/facade/cultures.facade';
import { BOProductSearchPage } from '@boels/models';
import { EMPTY, of } from 'rxjs';
import { selectProductSearchPageEntities } from '../selectors/product-search.selector';

@Injectable({
  providedIn: 'any',
})
export class ProductSearchEffects {
  private actions$ = inject(Actions);
  private store = inject<Store<BoelsState>>(Store);
  private productSearchGateway = inject(ProductSearchGateway);
  private cultureFacade = inject(CultureFacade);

  getProductSearchPage$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(GET_PRODUCT_SEARCH_PAGE),
      withLatestFrom(
        this.store.select(selectProductSearchPageEntities),
        this.cultureFacade.getActiveCulture(),
      ),
      switchMap(
        ([
          { currentPage, categoryId, sortCode, query },
          searchPageFromState,
          culture,
        ]) => {
          const stateEntity =
            searchPageFromState[
              culture + '_' + categoryId + '_' + sortCode + '_' + query
            ];

          // load only if searchPage is not in state or loaded already
          if (
            !stateEntity ||
            (stateEntity?.pagination?.totalPages !== undefined &&
              stateEntity?.pagination?.currentPage !== undefined &&
              stateEntity?.pagination.totalPages > currentPage &&
              stateEntity?.pagination.currentPage < currentPage)
          ) {
            return of(
              loadProductSearchPage({
                categoryId,
                currentPage,
                sortCode,
                culture,
                query,
              }),
            );
          }
          return EMPTY;
        },
      ),
    );
  });

  loadProductSearchPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LOAD_PRODUCT_SEARCH_PAGE),
      exhaustMap((action) => {
        return this.productSearchGateway
          .getProductSearchPageByCategoryId(
            action['categoryId'],
            action['currentPage'],
            action['sortCode'],
            action['query'],
          )
          .pipe(
            withLatestFrom(this.cultureFacade.getActiveCulture()),
            map(
              ([boProductSearchPage, culture]: [BOProductSearchPage, string]) =>
                loadProductSearchPageSuccess({
                  productSearchPage: boProductSearchPage,
                  sortCode: action['sortCode'],
                  culture,
                  query: action['query'],
                }),
            ),
            catchError((errorMessage) =>
              of(loadProductSearchPageFail({ errorMessage })),
            ),
            first(),
          );
      }),
    ),
  );
}
